<template>
  <div>
    <!-- 支払処理結果一覧 -->
    <BaseTableList
        :class="'payment-set-list' + ($isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.DETAIL) ? ' row-clickable-table' : '')"
        :table-set-list="paymentSetList"
        :cell-class-name="cellClassName"
        :display-items="rejectedPaymentSets"
        @row-click="onRowClick">
      <!-- オペレーションボタン -->
      <template
          slot="operation"
          slot-scope="{ row }">
        <BaseOperationTooltip
            v-if="showTooltip"
            :list="getTooltipList(row.row)"
            placement="bottom-end" />
      </template>
    </BaseTableList>

    <!-- 支払処理伝票ダイアログ -->
    <SlipDialog
        type="paymentSet"
        :visible.sync="detailDialogVisible"
        :detail-data="detailData" />

    <!-- 決済確定日登録ダイアログ -->
    <BaseDialog
        :title="`決済確定日${this.processType === 1 ? '登録' : '修正'}`"
        :visible.sync="paidAtVisible"
        :show-only-close-button="false"
        class="dateDialog"
        width="30%"
        :disable-ok-button="paidAtDisableOK"
        @closed="paidAtVisible = false"
        @cancel="paidAtVisible = false"
        @ok="onPaidAtOk">
      <template slot="contents">
        <div class="dialog-content">
          <BaseTableDetail
              :validate-scope="paidAtValidateScope"
              :editable="paidAtEditable"
              :table-set-list="paidAtTableSetList"
              :display-item="paidAtData" />
        </div>
      </template>
    </BaseDialog>
    <!-- 返金日登録ダイアログ -->
    <BaseDialog
        :title="`返金日${this.processType === 1 ? '登録' : '修正'}`"
        :visible.sync="regRefundDateVisible"
        :show-only-close-button="false"
        class="dateDialog"
        width="30%"
        :disable-ok-button="regRefundDateDisableOK"
        @closed="regRefundDateVisible = false"
        @cancel="regRefundDateVisible = false"
        @ok="onRegRefundDateOk">
      <template slot="contents">
        <div class="dialog-content">
          <BaseTableDetail
              :validate-scope="regRefundDateValidateScope"
              :editable="regRefundDateEditable"
              :table-set-list="regRefundDateTableSetList"
              :display-item="regRefundDateData" />
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.payment-set-list ::v-deep td.payment-set-status-error {
  color: #ff5a5a;
}
.dialog-content {
  margin: 0 20px;
}

.dateDialog .dialog-content {
  padding: 10px;
  display: flex;
  color: #9da6ba;
  font-weight: bold;
}

.dateDialog .dialog-content span {
  width: 150px;
  display: flex;
  align-items: center;
}

.dateDialog .dialog-content div,
.dateDialog .dialog-content  ::v-deep .el-date-editor.el-input {
  width: 100%;
}

</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import moment from 'moment'

import x from '@/components/mixins/mixin'
import tableRowAnimator from '@/components/mixins/tableRowAnimator'
import * as define from '@/define'
import * as config from '@/config'
import common from '@/utils/common'
import message from '@/utils/message'

import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import BaseTableList from '@/components/molecules/BaseTableList'
import BaseDialog from '@/components/molecules/BaseDialog'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'

import SlipDialog from '@/components/pages/order/SlipDialog'

const EDITABLE = Object.freeze({
  [define.ORDER_MODE.NORMAL]: Object.freeze({ paymentMethod: true, editableCondition: true })
})

const ERROR_REASON = {
  unprocessed: '処理待ち',
  processing: '処理中',
  processed: '処理済み',
  checking: '確認中',
  canceled: 'キャンセル済み',
  void: '無効なステータス',
  manualCompletion: '手動完了',
  errorEnd: 'エラー終了',
  errorProcessed: 'エラー処理済み'
}

const customValidationRules = (v) => {
  // 決済確定日は注文日時より未来であること
  v.$validator.extend('isAfterOrderDate', {
    getMessage: (args) => {
      return args + 'は注文日時より過去を指定することはできません'
    },
    validate: (data) => {
      if (data <= v.orderDatetime) {
        return false
      }
      return true
    }
  })
}

export default {
  name: 'OrderDetailPaymentSet',
  components: {
    BaseOperationTooltip,
    BaseTableList,
    SlipDialog,
    BaseDialog,
    BaseTableDetail
  },
  mixins: [x, tableRowAnimator],
  inject: ['parentValidator'],
  props: {
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    paymentSets: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      detailDialogVisible: false,
      detailData: {},
      isShownDialog: false,
      selectValue: null,
      movement: {},
      dialog: {},
      payment: [],
      changePayments: {
        srcPaymentSet: {},
        destPaymentSet: []
      },
      paidAtVisible: false,
      paidAtData: {
        paidAt: null
      },
      regRefundDateVisible: false,
      regRefundDateData: {
        refundDate: null
      },
      targetRow: null,
      processType: null
    }
  },
  computed: {
    define: () => define,
    ...mapGetters(
      'OrderDetail', {
        orderDetail: 'orderDetail',
        siteCode: 'siteCode',
        orderDatetime: 'orderDatetime'
      }),
    ...mapGetters({
      orderConfigManualPaidAt: 'orderConfigManualPaidAt'
    }),
    validateScope () {
      return 'OrderDetailItemsToolTip'
    },
    hasError () {
      return this.errors.any(this.validateScope)
    },
    rejectedPaymentSets () {
      // 支払処理結果一覧から除外
      const rows = _.reject(this.paymentSets, paymentSet => {
        // 処理コード: ダミー
        return paymentSet.processCode === define.PAYMENT_SET_PROCESS.DUMMY ||
        // 支払処理.ステータス: VOID
        paymentSet.statusCode === define.PAYMENT_SET_STATUS.VOID ||
        // 処理コード: (決済確認 || 返金確認) & 処理済み
          (_.includes([define.PAYMENT_SET_PROCESS.CAPTURE_CHEKKNG, define.PAYMENT_SET_PROCESS.REFUND_CHEKING], paymentSet.processCode) &&
              paymentSet.statusCode === define.PAYMENT_SET_STATUS.PROCESSED) ||
        // 支払処理区分: 支払処理不要
          paymentSet.orderPayment.paymentMethod.methodType === define.PAYMENT_METHOD_TYPE.NO_PROCESS
        // // 支払処理区分: (サイト決済 || サイト決済,返金連携なし,自動完了しない) & 処理コード: 決済
        //   (_.includes([define.PAYMENT_METHOD_TYPE.SITE, define.PAYMENT_METHOD_TYPE.SITE_NO_REFUND_NO_COMP], paymentSet.orderPayment.paymentMethod.methodType) &&
        //       paymentSet.processCode === define.PAYMENT_SET_PROCESS.CAPTURE)
      })
      return rows
    },
    // 支払処理結果一覧
    paymentSetList () {
      return [
        { label: '登録日時', path: 'createdAt', type: 'Date', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.CREATED_AT },
        { label: '支払方法', path: 'orderPayment.paymentMethod.name', type: 'String', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.PAYMENT_METHOD },
        { label: '処理タイプ', path: 'processCode', type: 'String', codeName: 'd_payment_set.process_code', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.PROCESS_TYPE },
        { label: '金額', path: 'amount', type: 'Number',
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.AMOUNT },
        { label: 'ステータス', path: 'statusCode', type: 'String', codeName: 'd_payment_set.status_code', columnKey: 'statusCode', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.STATUS },
        { label: '処理日時', path: 'processedAt', type: 'Date', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.PROCESSED_AT },
        { label: '', path: 'operation', type: 'Slot', class: 'right', columnKey: 'operation',
          isShow: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CANCEL) ||
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.RETRY) ||
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.ERROR_IGNORE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAYMENT) }
      ]
    },
    showTooltip () {
      return this.mode === define.ORDER_MODE.NORMAL
    },
    editable () {
      return EDITABLE[this.mode] || {}
    },
    regRefundDateEditable () {
      return { refundDate: true }
    },
    // 返金日登録
    regRefundDateTableSetList () {
      return [
        { label: '返金日',
          properties: [{ path: 'refundDate', type: 'Date', elementType: ELEMENT_TYPE.DATE,
            editableId: 'refundDate', validateRule: 'required|isAfterOrderDate:返金日', validateAs: '返金日' }]
        }
      ]
    },
    regRefundDateValidateScope () {
      return 'regRefundDate'
    },
    regRefundDateHasError () {
      return this.errors.any(this.regRefundDateValidateScope)
    },
    regRefundDateDisableOK () {
      return this.regRefundDateHasError || this.regRefundDateData.refundDate == null ||
        (this.processType === 2 && moment(this.regRefundDateData.refundDate).format('YYYY/MM/DD') === moment(this.targetRow.processedAt).format('YYYY/MM/DD'))
    },
    // 決済確定日
    paidAtEditable () {
      return { paidAt: true }
    },
    paidAtTableSetList () {
      return [
        { label: '決済確定日',
          properties: [{ path: 'paidAt', type: 'Date', elementType: ELEMENT_TYPE.DATE,
            editableId: 'paidAt', validateRule: 'required|isAfterOrderDate:決済確定日', validateAs: '決済確定日' }]
        }
      ]
    },
    paidAtValidateScope () {
      return 'paidAt'
    },
    paidAtHasError () {
      return this.errors.any(this.paidAtValidateScope)
    },
    paidAtDisableOK () {
      return this.paidAtHasError || this.paidAtData.paidAt == null ||
        (this.processType === 2 && moment(this.paidAtData.paidAt).format('YYYY/MM/DD') === moment(this.targetRow.orderPayment.paidAt).format('YYYY/MM/DD'))
    }
  },
  watch: {
    detailDialogVisible (val) {
      if (!val) {
        this.detailData = {}
      }
    }
  },
  created () {
    this.$validator = this.parentValidator
    customValidationRules(this)
  },
  methods: {
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row
     * @return {Array}
     */
    getTooltipList (row) {
      return [
        {
          title: 'キャンセル',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.UNPROCESSED || row.isCancellable !== 1 ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.CANCEL),
          click: (item) => this.cancel(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CANCEL)
        },
        {
          title: '再実行',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.RETRY),
          click: (item) => this.retry(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.RETRY)
        },
        {
          title: '手動完了',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.MANUAL_COMPLETION),
          click: (item) => this.manualCompletion(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.MANUAL_COMPLETION)
        },
        {
          title: '支払方法変更',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            !_.includes([define.PAYMENT_SET_PROCESS.CAPTURE, define.PAYMENT_SET_PROCESS.REFUND], row.processCode) ||
            row.amount === 0 || this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAYMENT),
          click: (item) => this.changePayment(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAYMENT)
        },
        {
          title: 'エラー解除',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.ERROR_IGNORE),
          click: (item) => this.errorIgnore(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.ERROR_IGNORE)
        },
        {
          title: '返金日登録',
          // 注文詳細画面モード=0 (通常) and ステータス=0 (未処理) and 処理コード = 4 (返金) and 支払処理区分 = N or N (自動連携しない)
          disabled: () => {
            return row.statusCode !== define.PAYMENT_SET_STATUS.UNPROCESSED || row.processCode !== define.PAYMENT_SET_PROCESS.REFUND || row.isReserved === 1 ||
            ![define.PAYMENT_METHOD_TYPE.CCS_NO_REFUND_NO_COMP, define.PAYMENT_METHOD_TYPE.SITE_NO_REFUND_NO_COMP].includes(_.get(row, 'orderPayment.paymentMethod.methodType', '')) ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.REGISTER_REFUND_DATE)
          },
          click: (item) => this.registerRefundDate(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.REGISTER_REFUND_DATE)
        },
        {
          title: '返金日修正',
          // 注文詳細画面モード=0 (通常) and ステータス=2 (処理済み) and 処理コード = 4 (返金)
          // and 支払処理区分 = 6 or 7 and 予約フラグ = 0 and 処理日時 is not null
          disabled: () => {
            return !(row.statusCode === define.PAYMENT_SET_STATUS.PROCESSED && row.processCode === define.PAYMENT_SET_PROCESS.REFUND &&
            [define.PAYMENT_METHOD_TYPE.CCS_NO_REFUND_NO_COMP, define.PAYMENT_METHOD_TYPE.SITE_NO_REFUND_NO_COMP].includes(_.get(row, 'orderPayment.paymentMethod.methodType', '')) &&
            row.isReserved === 0 && row.processedAt != null) ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_REFUND_DATE)
          },
          click: (item) => this.changeRefundDate(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_REFUND_DATE)
        },
        {
          title: '決済確定日修正',
          // 注文詳細画面モード=0 (通常) and ステータス=21 (手動完了) and 決済日時 is not null and 処理コード=3 (決済)
          // and サービスサイト設定「key1=manualCompletion,key2=manualPaidAt」のvalue1 = 1
          disabled: () => {
            return !(row.statusCode === define.PAYMENT_SET_STATUS.MANUALCOMPLETION && row.orderPayment.paidAt != null &&
              row.processCode === define.PAYMENT_SET_PROCESS.CAPTURE && this.orderConfigManualPaidAt === '1') ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAID_AT)
          },
          click: (item) => this.changePaidAt(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAID_AT)
        }
      ]
    },
    async callApi (url, data) {
      try {
        this.$loadingService.show()
        await common.apiAxios(url, data)
        this.$emit('requestUpdate') // 再ロード
      } catch (error) {
        const reason = _.get(ERROR_REASON, error.response.data.reason)
        reason ? common.addError(error, { msg: message.MSG_519({ reason }) }) : common.addError(error, { msg: message.MSG_902() })
        this.$notify.error({ error: error })
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    // キャンセル
    cancel (row) {
      this.$confirm('予約されている決済処理をキャンセルしますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}`,
          { method: 'delete' }
        )
      }).catch(e => e)
    },
    // 再実行
    retry (row) {
      const orderPayment = _.find(this.orderDetail.payments, { auid: row.orderPayment.auid })
      const msg = orderPayment.isProcessed !== 0 ? `支払処理を再実行しますか？`
        : `この支払処理は支払処理対象外に更新されています。
        再実行しても決済システムに連携せず、正常終了と見なします。
        支払処理を再実行しますか？`
      this.$confirm(msg, define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}/errorRetry`,
          { method: 'put' }
        )
      }).catch(e => e)
    },
    // 手動完了
    manualCompletion (row) {
      this.$confirm('手動完了しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.processType = 1 // 1:登録、2:修正
        // サービスサイト設定の値「key1=manualCompletion,key2=manualPaidAt」のvalue1が1のときダイアログを表示する
        if (this.orderConfigManualPaidAt === '1') {
          this.paidAtVisible = true
          this.paidAtData = { paidAt: null }
          this.targetRow = row
          this.$validator.reset(`${this.paidAtValidateScope}.*`)
        } else {
          this.callManualCompletionAndModPaidAtApi(row)
        }
      }).catch(e => e)
    },
    // 支払方法変更
    async changePayment (row) {
      const existReservedPaymentSet = _.find(this.paymentSets, paymentSet => {
        return paymentSet.orderPayment.auid === row.orderPayment.auid && paymentSet.orderPayment.id > row.orderPayment.id
      })

      if (existReservedPaymentSet) {
        this.$notify.info(`予約中の支払処理が存在します。
        既に金額が変更されている可能性があるため、金額を確認の上、保存してください。`)
      }

      // 変更元支払処理更新用のデータをセット
      this.changePayments.srcPaymentSet = {
        paymentSetUuid: row.uuid,
        uuid: row.orderPayment.uuid,
        paymentMethod: row.orderPayment.paymentMethod.name,
        amount: row.orderPayment.amount,
        isPaid: row.orderPayment.isPaid
      }
      const paymentAmount = row.amount  // 支払処理金額の取得

      this.$store.dispatch({
        type: 'set',
        states: {
          'Order/mode': define.ORDER_MODE.CHANGE_PAYMENT  // 支払方法変更モード
        }
      })

      await this.$emit('initByMode')

      // 変更元の支払処理をセット
      this.orderDetail.paymentSets = [ { uuid: row.uuid } ]

      // 変更元の注文支払の金額を更新
      const srcOrderPayment = _.find(this.orderDetail.payments, { auid: row.orderPayment.auid })
      srcOrderPayment.amount = +srcOrderPayment.amount - +paymentAmount

      // 変更先の注文支払の金額をセット(保留レコード)
      const holdOrderPayment = _.find(this.orderDetail.payments, { paymentMethod: { methodType: define.PAYMENT_METHOD_TYPE.HOLD } })
      if (holdOrderPayment) {
        holdOrderPayment.amount = +holdOrderPayment.amount + +paymentAmount
      } else {
        this.orderDetail.payments.push({
          paymentMethod: {
            code: define.PAYMENT_METHOD_TYPE.HOLD
          },
          amount: paymentAmount,
          isPaid: 1,
          isProcessed: 1,
          needsHoldOption: true
        })
      }

      // 支払内訳が表示されるようスクロール
      const paymentType = document.querySelector('#paymentType')
      if (paymentType) {
        window.scroll({ top: paymentType.offsetTop, behavior: 'smooth' })
      }
    },
    // エラー解除
    errorIgnore (row) {
      this.$confirm('エラー解除しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}/errorIgnore`,
          { method: 'put' }
        )
      }).catch(e => e)
    },
    // 返金日登録
    async registerRefundDate (row) {
      this.regRefundDateVisible = true
      this.regRefundDateData = { refundDate: null }
      this.targetRow = row
      this.processType = 1 // 1:登録、2:修正
      this.$validator.reset(`${this.regRefundDateValidateScope}.*`)
    },
    // 返金日修正
    async changeRefundDate (row) {
      this.regRefundDateVisible = true
      this.regRefundDateData = { refundDate: row.processedAt }
      this.targetRow = row
      this.processType = 2 // 1:登録、2:修正
      this.$validator.reset(`${this.regRefundDateValidateScope}.*`)
    },
    async onRegRefundDateOk () {
      await this.$validator.validate(`${this.regRefundDateValidateScope}.*`)
      if (!this.regRefundDateHasError) {
        this.$confirm(`返金日${this.processType === 1 ? '登録' : '修正'}しますか？`, define.MESSAGE_TITLE, {
          confirmButtonText: 'はい',
          cancelButtonText: 'いいえ',
          type: 'warning'
        }).then(async () => {
          this.regRefundDateVisible = false
          // 処理区分、1:登録、2:修正
          const method = this.processType === 1 ? 'regRefundDate' : 'modRefundDate'
          await this.callApi(
            `${config.PAYMENT_URL}/${this.targetRow.uuid}/` + method,
            { method: 'put', data: this.regRefundDateData }
          )
          this.$notify.success(`返金日${this.processType === 1 ? '登録' : '修正'}しました。`)
        }).catch(() => {})
      }
    },
    // 決済確定日
    async onPaidAtOk () {
      await this.$validator.validate(`${this.paidAtValidateScope}.*`)
      if (!this.paidAtHasError) {
        // チェックエラーがない場合、手動完了APIを呼びだす
        this.paidAtVisible = false
        this.callManualCompletionAndModPaidAtApi(this.targetRow)
      }
    },
    // 手動完了と決済確定日変更API
    async callManualCompletionAndModPaidAtApi (row) {
      const obj = {
        method: 'put'
      }
      // 決済確定日の入力がある場合、入力値。なければ、送らない
      if (this.paidAtData.paidAt) {
        this.$set(obj, 'data', this.paidAtData)
      }
      // 処理区分、1:登録、2:修正
      let method = ''
      let msg = ''
      if (this.processType === 1) {
        method = 'manualCompletion'
        msg = '手動完了しました。'
      } else {
        method = 'modPaidAt'
        msg = '決済確定日修正しました。'
      }
      await this.callApi(`${config.PAYMENT_URL}/${row.uuid}/` + method, obj)
      this.$notify.success(msg)
    },
    // 決済確定日修正
    async changePaidAt (row) {
      this.paidAtVisible = true
      this.paidAtData = { paidAt: row.orderPayment.paidAt }
      this.targetRow = row
      this.processType = 2 // 1:登録、2:修正
      this.$validator.reset(`${this.paidAtValidateScope}.*`)
    },
    editableCondition (row) {
      return row.uuid == null
    },
    cellClassName ({ row, column }) {
      if (column.columnKey !== 'statusCode') {
        return ''
      }

      return row.statusCode === 9 ? 'payment-set-status-error' : ''
    },
    onRowClick (row, column, event) {
      if (!this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.DETAIL)) {
        // 権限がない場合、何もしない
        return
      }
      if (column.columnKey === 'operation') {
        // tooltipのセルクリック時は何もしない
        return
      }
      this.detailData = row
      this.detailDialogVisible = true
    }
  }
}
</script>
